<template>
    <div class="image-compressor-page">
        <!-- 顶部标题栏 -->
        <div class="header-bar">
           <!-- <el-button class="back-button" icon="el-icon-arrow-left" @click="goToHome">
                返回工具箱
            </el-button>-->
            <h2 class="page-title">🖼️ 图片压缩工具</h2>
            <p class="page-subtitle">无损压缩图片，减小文件大小</p>
        </div>

        <div class="compressor-container">
            <el-row :gutter="30">
                <!-- 左侧：上传与设置区域 -->
                <el-col :span="12">
                    <el-card class="upload-card">
                        <div slot="header" class="card-header">
                            <span>上传图片</span>
                        </div>

                        <!-- 图片上传 -->
                        <div class="upload-area" @click="$refs.fileInput.click()">
                            <i class="el-icon-upload upload-icon"></i>
                            <div class="upload-text">点击上传图片或拖拽图片到此处</div>
                            <div class="upload-hint">支持 JPG, PNG, GIF 格式</div>
                            <input
                                    type="file"
                                    ref="fileInput"
                                    accept="image/*"
                                    @change="handleImageUpload"
                                    style="display: none;"
                            />
                        </div>

                        <!-- 原图信息 -->
                        <div v-if="originalUrl" class="image-info">
                            <el-row>
                                <el-col :span="12">
                                    <div class="info-item">
                                        <label>文件名:</label>
                                        <span>{{ originalFileName }}</span>
                                    </div>
                                </el-col>
                                <el-col :span="12">
                                    <div class="info-item">
                                        <label>原图尺寸:</label>
                                        <span>{{ originalWidth }} x {{ originalHeight }} px</span>
                                    </div>
                                </el-col>
                                <el-col :span="12">
                                    <div class="info-item">
                                        <label>文件大小:</label>
                                        <span>{{ originalSize }}</span>
                                    </div>
                                </el-col>
                            </el-row>
                        </div>

                        <!-- 压缩设置 -->
                        <div class="settings" v-if="originalUrl">
                            <h3>压缩设置</h3>
                            <el-form label-position="left" label-width="100px">
                                <el-form-item label="压缩比例">
                                    <el-slider
                                            v-model="quality"
                                            :min="0.1"
                                            :max="1"
                                            :step="0.1"
                                            show-input
                                            show-input-controls="false"
                                    />
                                </el-form-item>
                                <el-form-item label="目标宽度">
                                    <el-input-number
                                            v-model="targetWidth"
                                            :min="1"
                                            controls-position="right"
                                    /> px
                                </el-form-item>
                                <el-form-item label="目标高度">
                                    <el-input-number
                                            v-model="targetHeight"
                                            :min="1"
                                            controls-position="right"
                                    /> px
                                </el-form-item>
                                <el-form-item>
                                    <el-button
                                            type="primary"
                                            @click="compressImage"
                                            :loading="isCompressing"
                                            size="medium"
                                    >
                                        {{ isCompressing ? '压缩中...' : '开始压缩' }}
                                    </el-button>
                                </el-form-item>
                            </el-form>
                        </div>
                    </el-card>
                </el-col>

                <!-- 右侧：预览与结果区域 -->
                <el-col :span="12">
                    <el-card class="preview-card">
                        <div slot="header" class="card-header">
                            <span>预览与结果</span>
                        </div>

                        <!-- 原图预览 -->
                        <div v-if="originalUrl" class="preview-section">
                            <h3>原图预览</h3>
                            <div class="image-preview">
                                <img :src="originalUrl" alt="原图" />
                            </div>
                        </div>

                        <!-- 压缩后预览 -->
                        <div v-if="compressedUrl" class="preview-section">
                            <h3>压缩后预览</h3>
                            <div class="image-preview">
                                <img :src="compressedUrl" alt="压缩后" />
                            </div>
                            <div class="result-info">
                                <el-row>
                                    <el-col :span="12">
                                        <div class="info-item">
                                            <label>压缩尺寸:</label>
                                            <span>{{ compressedWidth }} x {{ compressedHeight }} px</span>
                                        </div>
                                    </el-col>
                                    <el-col :span="12">
                                        <div class="info-item">
                                            <label>压缩大小:</label>
                                            <span>{{ compressedSize }}</span>
                                        </div>
                                    </el-col>
                                    <el-col :span="12">
                                        <div class="info-item">
                                            <label>节省空间:</label>
                                            <span :class="savingsClass">{{ savings }}</span>
                                        </div>
                                    </el-col>
                                </el-row>
                            </div>
                            <a :href="compressedUrl" :download="compressedFileName" class="download-btn">
                                <el-button type="success" size="medium" icon="el-icon-download">
                                    下载压缩图片
                                </el-button>
                            </a>
                        </div>

                        <!-- 初始状态提示 -->
                        <div v-else class="initial-hint">
                            <i class="el-icon-picture-outline initial-icon"></i>
                            <p>请先上传一张图片</p>
                        </div>
                    </el-card>
                </el-col>
            </el-row>
        </div>

        <!-- 内容说明区 -->
        <div class="tool-content">
            <h2>🗜️ 在线图片压缩工具</h2>

            <div class="tool-content-intro">
                <p>智能压缩 JPG、PNG、GIF 图片，减小文件体积同时保持视觉质量。支持自定义压缩比例和输出尺寸，适合网页优化、社交媒体上传、邮件附件等场景。</p>
            </div>

            <div class="content-section">
                <h3><span class="icon">✨</span> 功能特点</h3>
                <ul>
                    <li><strong>智能压缩</strong>：自动优化算法，肉眼无明显质量损失</li>
                    <li><strong>格式全支持</strong>：JPG、PNG、GIF 均可压缩</li>
                    <li><strong>尺寸调节</strong>：可设置最大宽度/高度，自动等比缩放</li>
                    <li><strong>实时预览</strong>：压缩前后对比，直观查看效果</li>
                </ul>
            </div>

            <div class="content-section">
                <h3><span class="icon">📝</span> 使用教程</h3>
                <ol>
                    <li>上传需要压缩的图片（支持拖拽）</li>
                    <li>调整压缩比例滑块（建议 60-80%）</li>
                    <li>如需改变尺寸，设置最大宽度/高度</li>
                    <li>点击「开始压缩」并下载</li>
                </ol>
            </div>

            <div class="content-section">
                <h3><span class="icon">💡</span> 压缩建议</h3>
                <div class="tips-grid">
                    <div class="tip-card">
                        <strong>🌐 网页用图</strong>
                        <p>压缩至 70%，宽度 1200px 以内</p>
                    </div>
                    <div class="tip-card">
                        <strong>💬 微信/QQ</strong>
                        <p>压缩至 60%，宽度 800px 以内</p>
                    </div>
                    <div class="tip-card">
                        <strong>📧 邮件附件</strong>
                        <p>压缩至 50%，单张 500KB 以内</p>
                    </div>
                </div>
            </div>

            <div class="privacy-box">
                <span class="icon">🔒</span>
                <p><strong>隐私说明</strong>：使用 Canvas API 在浏览器本地压缩，图片不会上传到服务器。</p>
            </div>
        </div>
    </div>
</template>

<script>
    import seoMixin from '../js/seo-mixin';

export default {
        name: 'ImageCompressor',
        mixins: [seoMixin],
        data() {
            return {
                seoTitle: '在线图片压缩工具 - FreeToolBox',
                seoDescription: '免费在线图片压缩工具，无损压缩JPG、PNG、GIF图片，减小文件大小支持调整压缩比例、目标宽高。浏览器本地处理，保护隐私。',
                seoKeywords: '图片压缩,无损压缩,压缩图片,在线图片压缩,图片大小优化',
                originalUrl: null,
                compressedUrl: null,
                quality: 0.7,
                targetWidth: 800,
                targetHeight: 600,
                isCompressing: false,

                // 原图信息
                originalFileName: '',
                originalWidth: 0,
                originalHeight: 0,
                originalSize: '0 KB',

                // 压缩后信息
                compressedWidth: 0,
                compressedHeight: 0,
                compressedSize: '0 KB',
                savings: '0%',

                // 文件对象（用于获取大小）
                originalFile: null
            };
        },
        computed: {
            compressedFileName() {
                if (this.originalFileName) {
                    const ext = this.originalFileName.substring(this.originalFileName.lastIndexOf('.'));
                    return `compressed_${this.originalFileName.substring(0, this.originalFileName.lastIndexOf('.'))}${ext}`;
                }
                return 'compressed.jpg';
            },
            savingsClass() {
                const savingsValue = parseFloat(this.savings);
                if (savingsValue > 50) {
                    return 'high-savings';
                } else if (savingsValue > 20) {
                    return 'medium-savings';
                }
                return 'low-savings';
            }
        },
        methods: {
            goToHome() {
                // 假设引导页的路由名称为 'Home' 或路径为 '/'
                // 请根据您实际的路由配置选择其中一种方式

                // 方式一：通过路由名称跳转 (推荐)
                if (this.$router) {
                    this.$router.push({ name: 'Home' });
                }

                // 方式二：通过路由路径跳转
                // if (this.$router) {
                //   this.$router.push('/');
                // }

                // 方式三：如果在没有路由的简单环境中，可以尝试使用 window.location (不推荐用于SPA)
                // window.location.href = '/';
            },
            handleImageUpload(event) {
                const file = event.target.files[0];
                if (!file) return;

                // 清除之前的压缩结果
                this.compressedUrl = null;

                // 存储文件信息
                this.originalFile = file;
                this.originalFileName = file.name;
                this.originalSize = this.formatFileSize(file.size);

                // 显示原图预览
                this.originalUrl = URL.createObjectURL(file);

                const img = new Image();
                img.src = this.originalUrl;
                img.onload = () => {
                    this.originalWidth = img.width;
                    this.originalHeight = img.height;
                    // 默认设置目标尺寸为原图尺寸
                    this.targetWidth = img.width;
                    this.targetHeight = img.height;
                };
            },
            compressImage() {
                if (!this.originalUrl || this.isCompressing) return;

                this.isCompressing = true;

                const img = new Image();
                img.src = this.originalUrl;
                img.onload = () => {
                    const { targetWidth, targetHeight, quality } = this;

                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');

                    canvas.width = targetWidth;
                    canvas.height = targetHeight;

                    // 绘制缩放后的图片
                    ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

                    // 压缩并导出为 base64
                    canvas.toBlob(
                        blob => {
                            if (blob) {
                                this.compressedUrl = URL.createObjectURL(blob);
                                this.compressedWidth = targetWidth;
                                this.compressedHeight = targetHeight;
                                this.compressedSize = this.formatFileSize(blob.size);
                                this.calculateSavings(blob.size);
                            }
                            this.isCompressing = false;
                        },
                        'image/jpeg',
                        quality
                    );
                };
            },
            formatFileSize(bytes) {
                if (bytes === 0) return '0 Bytes';
                const k = 1024;
                const sizes = ['Bytes', 'KB', 'MB', 'GB'];
                const i = Math.floor(Math.log(bytes) / Math.log(k));
                return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
            },
            calculateSavings(compressedSize) {
                if (this.originalFile) {
                    const originalSize = this.originalFile.size;
                    const saved = originalSize - compressedSize;
                    const percentage = ((saved / originalSize) * 100).toFixed(2);
                    this.savings = `${percentage}%`;
                }
            }
        },
        beforeDestroy() {
            // 清理内存
            if (this.originalUrl) URL.revokeObjectURL(this.originalUrl);
            if (this.compressedUrl) URL.revokeObjectURL(this.compressedUrl);
        }
    };
</script>

<style scoped>
    .image-compressor-page {
        padding: 20px;
        background-color: #f5f7fa;
        min-height: 100vh;
    }

    .header-bar {
        text-align: center;
        margin-bottom: 30px;
        padding: 30px 20px;
        background: linear-gradient(120deg, #4775d8, #6a8cff);
        border-radius: 15px;
        color: white;
        position: relative;
    }

    .back-button {
        position: absolute;
        left: 20px;
        top: 50%;
        transform: translateY(-50%);
        background-color: rgba(255, 255, 255, 0.2) !important;
        border-color: rgba(255, 255, 255, 0.5) !important;
        color: white !important;
    }

    .back-button:hover {
        background-color: rgba(255, 255, 255, 0.3) !important;
    }

    .page-title {
        font-size: 32px;
        margin-bottom: 10px;
    }

    .page-subtitle {
        font-size: 18px;
        opacity: 0.9;
    }

    .compressor-container {
        max-width: 1200px;
        margin: 0 auto;
    }

    .card-header {
        font-weight: bold;
        font-size: 18px;
        color: #333;
    }

    .upload-card, .preview-card {
        border-radius: 10px;
        overflow: hidden;
        box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
    }

    .upload-area {
        border: 2px dashed #c0c4cc;
        border-radius: 10px;
        padding: 40px 20px;
        text-align: center;
        cursor: pointer;
        transition: all 0.3s ease;
        margin-bottom: 20px;
    }

    .upload-area:hover {
        border-color: #4775d8;
        background-color: #f0f5ff;
    }

    .upload-icon {
        font-size: 67px;
        color: #8c939d;
        margin-bottom: 15px;
    }

    .upload-text {
        font-size: 16px;
        color: #606266;
        margin-bottom: 10px;
    }

    .upload-hint {
        font-size: 14px;
        color: #909399;
    }

    .image-info, .result-info {
        background-color: #f9f9f9;
        border-radius: 8px;
        padding: 15px;
        margin-top: 20px;
    }

    .info-item {
        margin-bottom: 10px;
        display: flex;
    }

    .info-item label {
        width: 80px;
        font-weight: bold;
        color: #606266;
    }

    .info-item span {
        flex: 1;
        color: #333;
    }

    .settings h3, .preview-section h3 {
        margin-top: 0;
        margin-bottom: 15px;
        color: #333;
        border-bottom: 1px solid #eee;
        padding-bottom: 10px;
    }

    .image-preview {
        text-align: center;
        margin-bottom: 20px;
    }

    .image-preview img {
        max-width: 100%;
        max-height: 300px;
        border-radius: 8px;
        box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
    }

    .download-btn {
        display: block;
        text-align: center;
        margin-top: 20px;
    }

    .initial-hint {
        text-align: center;
        padding: 50px 0;
        color: #909399;
    }

    .initial-icon {
        font-size: 67px;
        margin-bottom: 15px;
    }

    .high-savings {
        color: #67c23a;
        font-weight: bold;
    }

    .medium-savings {
        color: #e6a23c;
        font-weight: bold;
    }

    .low-savings {
        color: #909399;
    }
    /* 内容说明区样式 */
    .tool-content {
        max-width: 900px;
        margin: 50px auto;
        padding: 0 20px;
        text-align: left;
    }

    .tool-content h2 {
        font-size: 1.8em;
        color: #333;
        margin-bottom: 20px;
        display: flex;
        align-items: center;
        gap: 12px;
    }

    .tool-content h2::before {
        content: '';
        display: inline-block;
        width: 5px;
        height: 32px;
        background: linear-gradient(180deg, #4775d8, #6a8cff);
        border-radius: 3px;
    }

    .tool-content-intro {
        background: linear-gradient(135deg, #f8faff 0%, #f0f5ff 100%);
        border-radius: 16px;
        padding: 25px 30px;
        margin-bottom: 30px;
        border: 1px solid #e8efff;
        box-shadow: 0 4px 15px rgba(71, 117, 216, 0.08);
    }

    .tool-content-intro p {
        color: #555;
        font-size: 15px;
        line-height: 1.9;
        margin: 0;
    }

    .content-section {
        background: #fff;
        border-radius: 16px;
        padding: 25px 30px;
        margin-bottom: 20px;
        box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
        border: 1px solid #f0f0f0;
        transition: box-shadow 0.3s ease;
    }

    .content-section:hover {
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    }

    .content-section h3 {
        color: #333;
        font-size: 1.15em;
        margin: 0 0 15px 0;
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .content-section h3 .icon {
        font-size: 1.2em;
    }

    .content-section ul, .content-section ol {
        margin: 0;
        padding-left: 24px;
    }

    .content-section li {
        color: #555;
        line-height: 2;
        margin-bottom: 8px;
        position: relative;
    }

    .content-section ul li::marker {
        color: #4775d8;
    }

    .content-section ol li::marker {
        color: #4775d8;
        font-weight: 600;
    }

    .content-section strong {
        color: #333;
    }

    .faq-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
        gap: 15px;
    }

    .faq-item {
        background: #fafbfc;
        border-radius: 12px;
        padding: 18px 20px;
        border: 1px solid #eee;
    }

    .faq-item p {
        margin: 0;
    }

    .faq-q {
        font-weight: 600;
        color: #333;
        margin-bottom: 8px !important;
        display: flex;
        align-items: flex-start;
        gap: 8px;
    }

    .faq-q::before {
        content: 'Q';
        background: #4775d8;
        color: #fff;
        font-size: 12px;
        padding: 2px 8px;
        border-radius: 4px;
        flex-shrink: 0;
    }

    .faq-a {
        color: #666;
        font-size: 14px;
        line-height: 1.7;
        padding-left: 28px;
    }

    .privacy-box {
        background: linear-gradient(135deg, #f0f9f0 0%, #e8f5e8 100%);
        border: 1px solid #c8e6c9;
        border-radius: 12px;
        padding: 18px 25px;
        display: flex;
        align-items: flex-start;
        gap: 12px;
    }

    .privacy-box .icon {
        font-size: 1.5em;
        flex-shrink: 0;
    }

    .privacy-box p {
        margin: 0;
        color: #2e7d32;
        line-height: 1.7;
    }

    .tips-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
        gap: 15px;
    }

    .tip-card {
        background: linear-gradient(135deg, #fff9e6 0%, #fff3cd 100%);
        border-radius: 12px;
        padding: 15px 18px;
        border: 1px solid #ffeeba;
    }

    .tip-card strong {
        color: #856404;
        display: block;
        margin-bottom: 6px;
    }

    .tip-card p {
        margin: 0;
        font-size: 13px;
        color: #856404;
    }
</style>



